import { concat, indexOf, isEmpty, map, pick, slice } from 'lodash';
import Constants from '../../Constants';
import * as actionTypes from '../../constants/actionTypes';
import { CONNECTOR_CODE_SQL_RUNNER } from 'js/constants/connectionCategories';
import { DELETE, GET, POST, PUT } from '../../constants/requestMethods';
import {
  ACLQueryModeTypes,
  AvailableFilterTypesByView,
  RESOURCE_SUGGESTIONS_COUNT,
  SearchFilterTypes,
  SearchFilterViews,
} from '../../constants/visibilityFilters';
import { processSearchFilters } from '../../lib/SearchFilterUtils';
import { canActivateResource, canUpdateResource } from '../../lib/flowValidationUtils';
import { CALL_PRIVATE_API } from '../../middleware/api';
import { getCurrentOrg, getCurrentUser } from '../../reducers/selectors';
import { docSchema, pipelineSchema } from '../../schemas';
import { setError } from '../request';
import { loadFlowsWithProjects, loadFlowsWithoutProjects } from './flowsByProject';
import { loadMCPFlows, loadMCPFlowsSearchSuggestions } from 'new/state/mcpFlows/mcpFlowsActions';

export const dataCardListLoadPipelines = (page, sortCriteria) => (dispatch) => {
  return dispatch(loadPipelines({ page, sort: sortCriteria, onlySearch: true }));
};

export const loadPipelines =
  ({
    page = undefined,
    sort = undefined,
    isPreview = false,
    projectId = undefined,
    onlySearch = false,
  }) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters, availableFilters },
        listing: { sortCriteria: listingSortCriteria },
      },
    } = getState();

    const aclQueryMode = accessControl?.mode ?? ACLQueryModeTypes.MODE_OWNER.key;

    const sortCriteria = sort || listingSortCriteria;
    const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.PIPELINES];
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];
    const { processedFilters } = processSearchFilters(
      onlySearch
        ? filters[SearchFilterViews.SEARCH_PIPELINES]
        : projectId
          ? concat(filters[SearchFilterViews.PROJECT_PIPELINES], {
              token: SearchFilterTypes.PROJECT_ID,
              value: projectId,
            })
          : filters[SearchFilterViews.PIPELINES],
      pick(availableFilters, availableFilterTypes),
    );

    let requestMethod;
    let endpoint;
    let payload;

    // Agentic (MCP Studio) flows are hidden from the standard flow list views.
    // They stay visible inside a Project, so the filter is skipped there.
    const agenticParam = projectId ? {} : { agentic: false };
    const agenticQuery = projectId ? '' : '&agentic=false';

    if (isEmpty(processedFilters)) {
      requestMethod = GET;
      endpoint = '/flows';
      payload = page
        ? {
            page,
            per_page: Constants.LISTING_ITEMS_PER_PAGE,
            sort_by: sortKey,
            sort_order: sortOrder,
            ...agenticParam,
          }
        : { ...agenticParam };
    } else {
      requestMethod = POST;
      endpoint = `/flows/search?page=${page}&per_page=${Constants.LISTING_ITEMS_PER_PAGE}&sort_by=${sortKey}&sort_order=${sortOrder}${agenticQuery}`;
      payload = { filters: processedFilters };
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_PIPELINES_REQUEST,
          actionTypes.GET_PIPELINES_SUCCESS,
          actionTypes.GET_PIPELINES_FAILURE,
        ],
        endpoint,
        payload,
        schema: pipelineSchema,
        requestMethod,
        aclQueryMode,
        requestInfo: {
          isPreview,
          view: onlySearch
            ? SearchFilterViews.SEARCH_PIPELINES
            : projectId
              ? SearchFilterViews.PROJECT_PIPELINES
              : SearchFilterViews.PIPELINES,
        },
      },
    }).catch((error) => console.log('loadPipelines error', error));
  };

export const loadProjectPipelinesByProject = (projectId) => (dispatch) => {
  return dispatch(
    loadPipelines({
      page: 1,
      isPreview: false,
      projectId,
    }),
  );
};

export const loadSqlAutomationsPipelines =
  ({
    page = 1,
    per_page = Constants.LISTING_ITEMS_PER_PAGE,
    sort = undefined,
    filters: filtersFromView = [],
    availableFilters = undefined,
  } = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        accessControl,
        visibilityFilters: { filters, availableFilters },
        listing: { sortCriteria: listingSortCriteria },
      },
    } = getState();

    const aclQueryMode = accessControl?.mode ?? ACLQueryModeTypes.MODE_OWNER.key;

    const sortCriteria = sort || listingSortCriteria;
    const availableFilterTypes = AvailableFilterTypesByView[SearchFilterViews.SQL_AUTOMATIONS];
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const filtersToUse = pick(availableFilters ?? {}, availableFilterTypes);
    const filtersWithConnector = concat(
      [
        {
          token: SearchFilterTypes.CONNECTOR_CODE,
          operator: 'eq',
          value: CONNECTOR_CODE_SQL_RUNNER,
        },
      ],
      filtersFromView,
    );
    const { processedFilters } = processSearchFilters(filtersWithConnector, filtersToUse);

    const endpoint = `/flows/search?page=${page}&per_page=${per_page}&sort_by=${sortKey}&sort_order=${sortOrder}&agentic=false`;
    const payload = { filters: processedFilters };

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_FLOWS_SQL_AUTOMATIONS_REQUEST,
          actionTypes.GET_FLOWS_SQL_AUTOMATIONS_SUCCESS,
          actionTypes.GET_FLOWS_SQL_AUTOMATIONS_FAILURE,
        ],
        endpoint,
        payload,
        schema: pipelineSchema,
        requestMethod: POST,
        aclQueryMode,
      },
    }).catch((error) => {
      console.error('loadSqlAutomationsPipelines error', error);
      throw error;
    });
  };

export function loadPipelinesSearchSuggestions(
  filters,
  availableFilters,
  projectId = undefined,
  onlySearch = false,
) {
  return (dispatch, getState) => {
    const { app } = getState();
    const aclQueryMode = app.accessControl.mode;

    const sortCriteria = Constants.SortCriteria.LAST_CREATED_DESC;
    const { sortKey, sortOrder } = Constants.SortCriteriaInfo[sortCriteria];

    const { processedFilters } = processSearchFilters(
      projectId
        ? concat(filters, {
            token: SearchFilterTypes.PROJECT_ID,
            value: projectId,
          })
        : filters,
      availableFilters,
    );

    if (isEmpty(processedFilters)) {
      return;
    }

    const params = new URLSearchParams({
      page: 1,
      per_page: RESOURCE_SUGGESTIONS_COUNT,
      sort_by: sortKey,
      sort_order: sortOrder,
      // Agentic flows stay visible inside a Project, hidden everywhere else
      ...(projectId ? {} : { agentic: false }),
    });

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.SEARCH_PIPELINES_REQUEST,
          actionTypes.SEARCH_PIPELINES_SUCCESS,
          actionTypes.SEARCH_PIPELINES_FAILURE,
        ],
        endpoint: `/flows/search?${params}`,
        schema: pipelineSchema,
        requestMethod: POST,
        aclQueryMode,
        payload: { filters: processedFilters },
        requestInfo: {
          view: onlySearch
            ? SearchFilterViews.SEARCH_PIPELINES
            : projectId
              ? SearchFilterViews.PROJECT_PIPELINES
              : SearchFilterViews.PIPELINES,
        },
      },
    });
  };
}

// the difference between loadPipeline and refreshPipeline is that the former
// clears the pipelines in the store whereas the latter just merges the
// loaded pipeline with the existing ones

export const loadPipeline =
  (flowNodeId, { isPreview, ignoreErrors } = { isPreview: true, ignoreErrors: false }) =>
  (dispatch, getState) => {
    const {
      app: {
        listing: { aclQueryMode },
      },
    } = getState();

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.GET_PIPELINES_REQUEST,
          actionTypes.GET_PIPELINES_SUCCESS,
          actionTypes.GET_PIPELINES_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}`,
        schema: pipelineSchema,
        requestMethod: GET,
        aclQueryMode,
        ignoreErrors,
        requestInfo: {
          isPreview,
        },
      },
    });
  };

export const refreshPipeline =
  (flowNodeId, { addToRoot = true, ignoreErrors } = {}, requested = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        listing: { aclQueryMode },
      },
    } = getState();

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.REFRESH_PIPELINE_REQUEST,
          actionTypes.REFRESH_PIPELINE_SUCCESS,
          actionTypes.REFRESH_PIPELINE_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}`,
        schema: pipelineSchema,
        requestMethod: GET,
        aclQueryMode,
        requestInfo: {
          addToRoot,
          nodeKey: flowNodeId,
        },
        ignoreErrors,
      },
    });
  };

export const getDenormalizedPipeline = (flowNodeId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_DENORMALIZED_PIPELINE_REQUEST,
        actionTypes.GET_DENORMALIZED_PIPELINE_SUCCESS,
        actionTypes.GET_DENORMALIZED_PIPELINE_FAILURE,
      ],
      endpoint: `/flows/${flowNodeId}`,
      requestMethod: GET,
      aclQueryMode: ACLQueryModeTypes.MODE_COLLABORATOR.searchValue,
    },
  });
};

// `options.ignoreErrors` suppresses the global error toast (errors are routed
// to the local error channel instead). Used by bulk operations, which report
// per-flow failures in their own progress modal.
export const pausePipeline =
  (flowNodeId, options = {}) =>
  (dispatch, getState) => {
    const {
      app: {
        listing: { aclQueryMode },
      },
    } = getState();

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.PAUSE_PIPELINE_REQUEST,
          actionTypes.PAUSE_PIPELINE_SUCCESS,
          actionTypes.PAUSE_PIPELINE_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/pause`,
        schema: pipelineSchema,
        requestMethod: PUT,
        aclQueryMode,
        ignoreErrors: options.ignoreErrors,
      },
    });
  };

export const activatePipeline =
  (flowNodeId, runNow = false, options = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      entities: { flowNodes },
    } = state;

    const flowNode = flowNodes[flowNodeId];
    const validation = canActivateResource(flowNode);

    if (!validation.canActivate) {
      const error = { message: validation.reason, code: 'ACTIVATION_BLOCKED' };
      // Bulk runs pass ignoreErrors → route to the local error channel (no toast);
      // single-flow callers keep the default global toast.
      dispatch(options.ignoreErrors ? setError(error, true) : setError(error));
      return Promise.reject(error);
    }

    const {
      app: {
        listing: { aclQueryMode },
      },
    } = state;

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.ACTIVATE_PIPELINE_REQUEST,
          actionTypes.ACTIVATE_PIPELINE_SUCCESS,
          actionTypes.ACTIVATE_PIPELINE_FAILURE,
        ],
        endpoint: `/flows/${flowNodeId}/activate?run_now=${runNow}`,
        schema: pipelineSchema,
        requestMethod: PUT,
        aclQueryMode,
        ignoreErrors: options.ignoreErrors,
      },
    });
  };

export const deletePipeline = (flowNodeId) => (dispatch, getState) => {
  const {
    entities: { flowNodes },
    app: {
      listing: { aclQueryMode },
    },
    pipelines: { branches },
  } = getState();
  const branchItems = branches[flowNodeId]?.items;
  const deletedItems = map(
    slice(branchItems, indexOf(branchItems, flowNodeId)),
    (id) => flowNodes[id],
  );

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.DELETE_PIPELINE_REQUEST,
        actionTypes.DELETE_PIPELINE_SUCCESS,
        actionTypes.DELETE_PIPELINE_FAILURE,
      ],
      endpoint: `/flows/${flowNodeId}`,
      schema: pipelineSchema,
      requestMethod: DELETE,
      aclQueryMode,
      requestInfo: {
        nodeKey: flowNodeId,
        deletedItems,
      },
    },
  }).then(() => {
    // if root node wasn't deleted, refresh pipeline
    const rootNodeKey = branches[flowNodeId].items[0];

    if (rootNodeKey !== flowNodeId) {
      return dispatch(refreshPipeline(rootNodeKey));
    }
  });
};

export const selectPipelineItem = (nodeKey) => {
  return {
    type: actionTypes.SELECT_PIPELINE_ITEM,
    nodeKey,
  };
};

export const deselectAllPipelineItems = (nodeKey) => {
  return {
    type: actionTypes.DESELECT_ALL_PIPELINE_ITEMS,
    nodeKey,
  };
};

export const setPipelineAction = (flowNodeId = null, pipelineActionType = null) => {
  return {
    type: actionTypes.SET_PIPELINE_ACTION,
    pipelineAction: flowNodeId ? { flowNodeId, pipelineActionType } : null,
  };
};

export const setDetailViewCard =
  (selectedCard, preSelectedTab = null) =>
  (dispatch) => {
    return dispatch({
      type: actionTypes.SET_DETAIL_VIEW_CARD,
      selectedCard,
      preSelectedTab,
    });
  };

export const removeDetailViewCard = () => (dispatch) => {
  return dispatch({ type: actionTypes.REMOVE_DETAIL_VIEW_CARD });
};

export const fetchPipelineDoc = (flowNodeId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.GET_PIPELINE_DOC_REQUEST,
        actionTypes.GET_PIPELINE_DOC_SUCCESS,
        actionTypes.GET_PIPELINE_DOC_FAILURE,
      ],
      endpoint: `/flows/${flowNodeId}/docs`,
      schema: [docSchema],
      requestMethod: GET,
      payload: {
        expand: 1,
      },
      requestInfo: {
        id: flowNodeId,
      },
    },
  });
};

export const updatePipelineDoc = (flowNodeId, content) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.UPDATE_PIPELINE_DOC_REQUEST,
        actionTypes.UPDATE_PIPELINE_DOC_SUCCESS,
        actionTypes.UPDATE_PIPELINE_DOC_FAILURE,
      ],
      endpoint: `/flows/${flowNodeId}/docs?expand=1`,
      schema: [docSchema],
      requestMethod: POST,
      payload: {
        docs: [
          {
            name: `Doc for dataflow ${flowNodeId}`,
            description: `Doc for dataflow ${flowNodeId}`,
            doc_type: 'md',
            text: content,
          },
        ],
      },
      requestInfo: {
        id: flowNodeId,
      },
    },
  });
};

export const updatePipelineInfo =
  (flowId, newInfo = {}) =>
  (dispatch, getState) => {
    const state = getState();
    const {
      entities: { flowNodes },
    } = state;

    const flowNode = flowNodes[flowId];
    const validation = canUpdateResource(flowNode);

    if (!validation.canActivate) {
      const error = { message: validation.reason, code: 'UPDATE_BLOCKED' };
      dispatch(setError(error));
      return Promise.reject(error);
    }

    return dispatch({
      [CALL_PRIVATE_API]: {
        types: [
          actionTypes.UPDATE_PIPELINE_INFO_REQUEST,
          actionTypes.UPDATE_PIPELINE_INFO_SUCCESS,
          actionTypes.UPDATE_PIPELINE_INFO_FAILURE,
        ],
        endpoint: `/flows/${flowId}?expand=1`,
        schema: pipelineSchema,
        requestMethod: PUT,
        payload: newInfo,
      },
    });
  };

export const cloneFlow = (data, payload) => (dispatch, getState) => {
  const { flow_node_id } = data;
  const state = getState();
  const user = getCurrentUser(state);
  const org = getCurrentOrg(state);

  if (!isEmpty(user) && !isEmpty(org)) {
    payload.owner_id = user.id;
    payload.org_id = org.id;
  }

  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.CLONE_FLOW_REQUEST,
        actionTypes.CLONE_FLOW_SUCCESS,
        actionTypes.CLONE_FLOW_FAILURE,
      ],
      endpoint: `/flows/${flow_node_id}/copy`,
      schema: pipelineSchema,
      requestMethod: POST,
      payload: payload,
      requestInfo: {
        id: data.id,
      },
    },
  });
};

export const createRAGFlow = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.CREATE_RAG_FLOW_REQUEST,
        actionTypes.CREATE_RAG_FLOW_SUCCESS,
        actionTypes.CREATE_RAG_FLOW_FAILURE,
      ],
      endpoint: '/flows/import',
      payload: { template_type: 'rag' },
      schema: pipelineSchema,
      requestMethod: POST,
    },
  });
};

export const publish = (flowId) => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.PUBLISH_RAG_FLOW_REQUEST,
        actionTypes.PUBLISH_RAG_FLOW_SUCCESS,
        actionTypes.PUBLISH_RAG_FLOW_FAILURE,
      ],
      endpoint: `/flows/${flowId}/rag/publish`,
      payload: {},
      schema: pipelineSchema,
      requestMethod: PUT,
    },
  });
};

export const createApiServerFlow = () => (dispatch) => {
  return dispatch({
    [CALL_PRIVATE_API]: {
      types: [
        actionTypes.CREATE_API_SERVER_FLOW_REQUEST,
        actionTypes.CREATE_API_SERVER_FLOW_SUCCESS,
        actionTypes.CREATE_API_SERVER_FLOW_FAILURE,
      ],
      endpoint: '/flows/import',
      payload: { template_type: 'api_server' },
      schema: pipelineSchema,
      requestMethod: POST,
    },
  });
};

export {
  loadFlowsWithProjects,
  loadFlowsWithoutProjects,
  loadMCPFlows,
  loadMCPFlowsSearchSuggestions,
};
